import dayjs from 'dayjs';
import { strapi } from 'networking';
import { InferGetStaticPropsType } from 'next';
import Image from 'next/image';
import { StrapiArticlesResponse, StrapiEventResponse, StrapiImageBase, StrapiMediaResponse } from 'types';
import hero from '../assets/news/hero-new.jpg';
import { Button } from '../components/general/button';
import { A } from '../components/general/link';
import { BlueArch, WhiteArch } from '../components/img/svgs';
import { Meta } from '../components/meta/meta';

// dream big 2023
import dreamBig from '../assets/news/dream-big.png';

// home team tailgate 2024
import Link from 'next/link';
import homeTeamTailgate2024Image from '../assets/events/tailgate-hero.jpeg';

export async function getStaticProps() {
  const mediaParams = {
    populate: { thumbnail: { fields: ['name', 'url', 'height', 'width'] } },
    pagination: { page: 1, pageSize: 10 },
    sort: ['id:desc'],
  };

  const mediaReq = strapi.get<StrapiMediaResponse>(`/api/medias`, { params: mediaParams });

  const articleParams = {
    pagination: { page: 1, pageSize: 10 },
    sort: ['id:desc'],
  };
  const articlesReq = strapi.get<StrapiArticlesResponse>(`/api/articles`, { params: articleParams });

  const eventParams = {
    populate: { image: { fields: ['name', 'url', 'height', 'width'] } },
    pagination: { page: 1, pageSize: 12 },
    sort: ['date:asc'],
  };
  const eventReq = strapi.get<StrapiEventResponse>(`/api/events`, { params: eventParams });

  const [media, articles, events] = await Promise.all([
    mediaReq.then(([res]) => res.data.map(p => ({ id: p.id, ...p.attributes }))),
    articlesReq.then(([res]) => res.data.map(p => ({ id: p.id, ...p.attributes }))),
    eventReq.then(([res]) => res.data.map(p => ({ id: p.id, ...p.attributes }))),
  ]);

  return {
    props: { media, articles, events },
    revalidate: 21600,
  };
}

export default function News({ media, articles, events }: InferGetStaticPropsType<typeof getStaticProps>) {
  const mediaAndArticles = [...media, ...articles].sort((a, b) => {
    const aDate = 'published' in a ? new Date(a.published) : new Date();
    const bDate = 'published' in b ? new Date(b.published) : new Date();
    return bDate.getTime() - aDate.getTime();
  });
  return (
    <main>
      <Meta title="news &amp; events" description="Beds for Kids News and Events" />
      <section className="relative h-[70vh] w-full overflow-hidden">
        <Image
          alt="Beds for Kids Corporate Volunteers"
          src={hero}
          className="absolute z-0 h-full w-full object-cover object-[center_33%]"
          sizes="100vw"
          priority
        />
        <div className="pointer-events-none absolute -bottom-[1px] left-0 right-0">
          <WhiteArch />
        </div>
      </section>
      <section className="bg-white pb-12 text-blue-500">
        <h1 className="mx-auto my-4 max-w-3xl text-center font-operetta text-6xl">news &amp; events</h1>
        <p className="mx-auto max-w-3xl px-4 text-center font-avenir text-xl leading-6">
          Explore the latest updates, upcoming events and important milestones shaping our impact across the Charlotte
          region.
        </p>
      </section>
      <section className="flex flex-col items-center bg-white pb-12 text-blue-500">
        <div className="grid w-full max-w-4xl gap-x-24 gap-y-8 px-4 pb-8 font-avenir md:grid-cols-5">
          <div className="md:col-span-2">
            <h2 className="mb-8 text-3xl font-medium">In the News</h2>
            <div className="flex max-h-[80vh] flex-col gap-4 overflow-scroll">
              {mediaAndArticles.map(m => (
                <>
                  {/* {'published' in m && (
                    <div key={`article_${m.id}`} className="flex flex-col border-b-2 border-blue-500 pb-4">
                      <A href={m.url} external>
                        <h3 className="mb-2 text-xl font-medium">{m.title}</h3>
                        <p className="leading-4">by {m.by}</p>
                        <time>{dayjs(m.published).format('dddd, MMMM D, YYYY')}</time>
                      </A>
                    </div>
                  )} */}
                  {'type' in m && (
                    <div key={`media_${m.id}`} className="flex flex-col text-white">
                      <A href={m.url} external className="relative aspect-[16/9] overflow-hidden">
                        <Image
                          src={m.thumbnail.data.attributes.url}
                          alt={m.thumbnail.data.attributes.name}
                          fill={true}
                          className="absolute inset-0 object-cover object-[0_20%]"
                          sizes="100vw, (min-width: 768px) 33vw"
                        />
                        <div className="absolute inset-0 bg-[#0a0a19] opacity-[45%]" />
                        <div className="absolute inset-2 px-2">
                          {/* <p>{m.type}</p> */}
                          <h3 className="absolute top-2 font-medium leading-5">{m.title}</h3>
                        </div>
                      </A>
                    </div>
                  )}
                </>
              ))}
            </div>
          </div>
          <div className="md:col-span-3">
            <h2 className="mb-8 text-3xl font-medium">Upcoming Events</h2>
            <div className="flex max-h-[80vh] flex-col gap-4 overflow-scroll">
              {/* Check back soon for future event announcements and invitations! */}
              <div>
                <div className="mb-2 text-xl font-bold">Celebrating 15 Years of Furnishing Futures</div>

                <p>
                  Thank you to all who joined us for our ribbon-cutting celebration! Continue to follow along at{' '}
                  <Link href="/15" className="font-bold text-green-500 underline hover:text-red-500">
                    bedsforkids.org/15
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* <section className="flex flex-col items-center bg-white pb-24 text-blue-500">
        <div className="w-full max-w-4xl px-4">
          <div>
            <h2 className="mx-auto my-4 max-w-3xl text-center font-operetta text-6xl">events</h2>
            <p className="mx-auto max-w-3xl px-4 text-center font-avenir text-xl leading-6">
              Across the year we host events to increase awareness for Beds for Kids and fundraise to further our
              mission. We would love to have you join the next one!
            </p>
          </div>
          <div className="text-avenir grid gap-x-4 gap-y-8 pt-8 md:grid-cols-2 md:gap-x-8 md:gap-y-16">
            <HomeTeamTailgate2024 />
            {events.map(e => (
              <Event key={`event_${e.id}`} event={e} />
            ))}
          </div>
        </div>
      </section> */}
      <BlueArch />
    </main>
  );
}

function Event({
  event,
}: {
  event: {
    name: string;
    date: string;
    information: string;
    location: string;
    link: string;
    image: StrapiImageBase;
    id: number;
  };
}) {
  return (
    <div className="flex flex-col text-lg leading-6">
      <Image
        src={event.image.data.attributes.url}
        height={event.image.data.attributes.height}
        width={event.image.data.attributes.width}
        alt={event.image.data.attributes.name}
        className="aspect-[1.5] w-full rounded-xl object-cover"
        // className="aspect-2 overflow-hidden rounded-xl object-cover"
        sizes="100vw, (min-width: 768px) 50vw"
      />
      <h4 className="mt-4 text-center text-3xl font-semibold">{event.name}</h4>
      <p className="mt-4">{event.information}</p>
      <ul className="my-8 ml-4 flex flex-col gap-1 border-l-2 border-green-500 pl-4 font-medium lowercase">
        <li className="capitalize">- {dayjs(event.date).format('dddd, MMMM D, YYYY')}</li>
        <li className="capitalize">- {event.location}</li>
      </ul>
      {event.link && (
        <div className="w-full text-center">
          <Button external link={event.link}>
            More Info
          </Button>
        </div>
      )}
    </div>
  );
}

function HomeTeamTailgate2024() {
  return (
    <div className="flex flex-col text-lg leading-6">
      <Image
        src={homeTeamTailgate2024Image}
        alt="Beds for Kids Home Team Tailgate Hero"
        className="aspect-[1.5] w-full rounded-xl object-cover"
        // className="aspect-2 overflow-hidden rounded-xl object-cover"
        sizes="100vw, (min-width: 768px) 50vw"
      />
      <h4 className="mt-4 text-center text-3xl font-semibold">Home Team Tailgate</h4>
      <p className="mt-4">
        Join us for an evening at the warehouse to raise funds for Beds for Kids and spend time with our staff! Swing by
        for a tour, eat barbecue, and play some games.
      </p>
      <ul className="my-8 ml-4 flex flex-col gap-1 border-l-2 border-green-500 pl-4 font-medium lowercase">
        <li className="capitalize">- Friday, October 25th, 2024 5-8PM</li>
        <li className="capitalize">- 4200 Revolution Park Drive, 28217</li>
        <li className="capitalize">- BBQ Dinner Provided</li>
        <li className="capitalize">- Casual Attire</li>
      </ul>
      <div className="w-full text-center">
        <Button link="/events/home-team-tailgate-2024">More Info</Button>
      </div>
    </div>
  );
}

function _DreamBigEvent() {
  return (
    <div className="flex flex-col text-lg leading-6">
      <Image
        src={dreamBig}
        alt="Beds for Kids annual golf tournament"
        className="rounded-xl object-cover"
        // className="aspect-2 overflow-hidden rounded-xl object-cover"
        sizes="100vw, (min-width: 768px) 50vw"
      />
      <h4 className="mt-4 text-center text-3xl font-semibold">Dream Big Benefit</h4>
      <p className="mt-4">
        Dream Big is an annual fundraising event hosted by Beds for Kids and sponsored by corporate donors and
        foundations. The 2022 event raised over $157,000 to further the mission of Beds for Kids and further the goal of
        delivering 2,500 beds in 2022.
      </p>
      <ul className="my-8 ml-4 flex flex-col gap-1 border-l-2 border-green-500 pl-4 font-medium lowercase">
        <li className="capitalize">- Thursday, May 4th, 2023</li>
        <li className="capitalize">- The Ruth By Beau Monde</li>
      </ul>
    </div>
  );
}
